import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MenuScreen from '@/components/MenuScreen';
import JoinRoom from '@/components/JoinRoom';
import Lobby from '@/components/Lobby';
import GameRoom from '@/components/GameRoom';
import GameEnd from '@/components/GameEnd';
import PracticeMode from '@/components/PracticeMode';
import { SettingsToggles } from '@/components/SettingsToggles';
import { useGameStore, RoomState, Player } from '@/lib/gameStore';
import { supabase } from '@/integrations/supabase/client';
import { useBackgroundMusic } from '@/hooks/useBackgroundMusic';
import { useToast } from '@/hooks/use-toast';
import confetti from 'canvas-confetti';

type Screen = 'menu' | 'join' | 'lobby' | 'game' | 'end' | 'practice';

const Index = () => {
  const [currentScreen, setCurrentScreen] = useState<Screen>('menu');
  const { reset, currentRoom, playerId, playerName, setCurrentRoom, setPlayerId } = useGameStore();
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const { start, stop, setIntensity, isMuted, toggleMute } = useBackgroundMusic();
  const [sessionRecovered, setSessionRecovered] = useState(false);
  const [pendingRoomCode, setPendingRoomCode] = useState<string | undefined>();
  const { toast } = useToast();

  // Handle rematch accepted - transition to new room
  const handleRematchAccepted = useCallback(async (newRoomId: string) => {
    try {
      // Fetch the new room data
      const { data: room, error: roomError } = await supabase
        .from('rooms')
        .select('*')
        .eq('id', newRoomId)
        .single();

      if (roomError || !room) {
        throw new Error('Could not find new room');
      }

      // Fetch players in the new room
      const { data: players, error: playersError } = await supabase
        .from('players')
        .select('*')
        .eq('room_id', newRoomId);

      if (playersError) {
        throw playersError;
      }

      // Find my player record in the new room (match by auth_user_id)
      const { data: { session } } = await supabase.auth.getSession();
      const myPlayer = players?.find(p => p.auth_user_id === session?.user?.id);

      if (!myPlayer) {
        throw new Error('Could not find your player record');
      }

      // Map players to the expected format
      const mappedPlayers: Player[] = (players || []).map(p => ({
        id: p.id,
        name: p.name,
        isHost: p.is_host || false,
        stressLevel: (p.stress_level as 'calm' | 'nervous' | 'panic') || 'calm',
        isActive: p.is_active || true,
      }));

      // Update game store with new room state
      const newRoomState: RoomState = {
        id: room.id,
        hostId: room.host_id,
        status: (room.status as 'menu' | 'lobby' | 'playing' | 'finished') || 'lobby',
        currentRound: room.current_round || 0,
        currentTotal: room.current_total || 0,
        targetTotal: room.target_total || 67,
        players: mappedPlayers,
        hasSelected: false,
        roundTimeRemaining: 15,
      };

      setCurrentRoom(newRoomState);
      setPlayerId(myPlayer.id);

      // Trigger celebration
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      toast({
        title: "🎉 New game starting!",
        description: `Joined room ${newRoomId} with ${mappedPlayers.length} players`,
      });

      // Navigate to lobby
      setCurrentScreen('lobby');
      navigate('/');
    } catch (error) {
      console.error('Error joining rematch room:', error);
      toast({
        title: "Error",
        description: "Failed to join new room",
        variant: "destructive",
      });
    }
  }, [setCurrentRoom, setPlayerId, toast, navigate]);

  // Start music on first user interaction
  useEffect(() => {
    const handleStart = () => {
      start();
      document.removeEventListener('click', handleStart);
      document.removeEventListener('keydown', handleStart);
    };

    document.addEventListener('click', handleStart);
    document.addEventListener('keydown', handleStart);

    return () => {
      document.removeEventListener('click', handleStart);
      document.removeEventListener('keydown', handleStart);
      stop();
    };
  }, [start, stop]);

  // Session recovery - check for existing persisted session
  useEffect(() => {
    if (sessionRecovered || !currentRoom || !playerId) return;

    const recoverSession = async () => {
      try {
        // Verify auth session is still valid
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          reset();
          return;
        }

        // Verify room still exists
        const { data: room, error: roomError } = await supabase
          .from('rooms')
          .select('*')
          .eq('id', currentRoom.id)
          .single();

        if (roomError || !room) {
          reset();
          return;
        }

        // Verify player record still exists
        const { data: player, error: playerError } = await supabase
          .from('players')
          .select('*')
          .eq('id', playerId)
          .single();

        if (playerError || !player) {
          reset();
          return;
        }

        // Check for stale games - if 'playing' with round 0 for too long, or very old last_updated
        const lastUpdated = new Date(room.last_updated).getTime();
        const now = Date.now();
        const isStaleGame = 
          (room.status === 'playing' && room.current_round === 0 && (now - lastUpdated) > 5 * 60 * 1000) ||
          ((now - lastUpdated) > 10 * 60 * 1000);

        if (isStaleGame) {
          toast({
            title: "Previous game expired",
            description: "Starting fresh. Your previous game was inactive for too long.",
          });
          reset();
          setSessionRecovered(true);
          return;
        }

        // Auto-redirect based on room status
        if (room.status === 'playing') {
          setCurrentScreen('game');
        } else if (room.status === 'lobby') {
          setCurrentScreen('lobby');
        } else if (room.status === 'finished') {
          setCurrentScreen('end');
        }

        setSessionRecovered(true);
      } catch (error) {
        console.error('Session recovery error:', error);
        reset();
      }
    };

    recoverSession();
  }, [currentRoom, playerId, reset, sessionRecovered]);

  // Handle deep linking - check if player has a name before going to join
  useEffect(() => {
    if (roomCode && !sessionRecovered) {
      if (playerName.trim()) {
        // User has a name, go directly to join
        setCurrentScreen('join');
      } else {
        // No name yet, stay on menu but store the pending room code
        setPendingRoomCode(roomCode);
        setCurrentScreen('menu');
        toast({
          title: "Enter your name first",
          description: `You'll join room ${roomCode} after entering your name`,
        });
      }
    }
  }, [roomCode, sessionRecovered, playerName, toast]);

  // Subscribe to room status changes for automatic navigation
  useEffect(() => {
    if (!currentRoom) return;

    const channel = supabase
      .channel(`index-room:${currentRoom.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'rooms',
          filter: `id=eq.${currentRoom.id}`
        },
        (payload) => {
          const newRoom = payload.new as any;
          
          // Only handle game->end transition here
          // Lobby->game transition is handled by Lobby component
          if (newRoom.status === 'finished' && currentScreen === 'game') {
            setCurrentScreen('end');
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentRoom?.id, currentScreen]);

  const handleNavigate = (screen: Screen) => {
    setCurrentScreen(screen);
  };

  const handleLeave = () => {
    // Mark tutorial as seen so it doesn't re-show on leave
    localStorage.setItem('67chaos-tutorial-seen', 'true');
    reset();
    navigate('/');
    setCurrentScreen('menu');
  };

  return (
    <div className="min-h-screen bg-background">
      {currentScreen === 'menu' && (
        <MenuScreen 
          onNavigate={handleNavigate} 
          setMusicIntensity={setIntensity}
          pendingRoomCode={pendingRoomCode}
          onClearPendingRoomCode={() => setPendingRoomCode(undefined)}
        />
      )}
      
      {currentScreen === 'join' && (
        <JoinRoom onNavigate={handleNavigate} initialRoomCode={roomCode} />
      )}
      
      {currentScreen === 'lobby' && (
        <Lobby 
          onStartGame={() => handleNavigate('game')} 
          onLeave={handleLeave}
          setMusicIntensity={setIntensity}
        />
      )}

      {currentScreen === 'game' && (
        <GameRoom 
          onGameEnd={() => handleNavigate('end')} 
          onLeave={handleLeave}
          setMusicIntensity={setIntensity}
        />
      )}

      {currentScreen === 'end' && (
        <GameEnd 
          onPlayAgain={() => handleNavigate('lobby')}
          onBackToMenu={handleLeave}
          setMusicIntensity={setIntensity}
          onRematchAccepted={handleRematchAccepted}
        />
      )}

      {currentScreen === 'practice' && (
        <PracticeMode 
          onBack={() => handleNavigate('menu')}
          setMusicIntensity={setIntensity}
        />
      )}

      <SettingsToggles isMuted={isMuted} onToggleMute={toggleMute} />
    </div>
  );
};

export default Index;